/**
 * V4.42 · DNAPay · Configuração da Régua de Notificações Financeiras
 *
 * Endpoints consumidos:
 *   GET/PUT   /api/dnapay/notifications-admin/settings
 *   GET/PUT   /api/dnapay/notifications-admin/templates[/:event]
 *   GET       /api/dnapay/notifications-admin/jobs
 *   GET       /api/dnapay/notifications-admin/metrics
 *   POST      /api/dnapay/notifications-admin/regua/run-now
 *   POST      /api/dnapay/notifications-admin/dispatch-now
 *   POST      /api/dnapay/notifications-admin/jobs/:id/cancel
 *   POST      /api/dnapay/notifications-admin/jobs/:id/resend
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Bell, Clock, Calendar, Send, PlayCircle, Search, RefreshCw, Save, AlertCircle, CheckCircle2, XCircle, Undo2, Sparkles } from 'lucide-react';

const WEEKDAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const EVENT_LABEL = {
  NEW_TITLE: 'Novo boleto disponível',
  DUE_SOON_D7: 'Vencimento em 7 dias (D-7)',
  DUE_SOON_D3: 'Vencimento em 3 dias (D-3)',
  DUE_SOON_D1: 'Vencimento amanhã (D-1)',
  DUE_TODAY: 'Vence hoje (D0)',
  OVERDUE_D1: 'Vencido 1 dia (D+1)',
  OVERDUE_D5: 'Vencido 5 dias (D+5)',
  OVERDUE_D10: 'Vencido 10 dias (D+10)',
  OVERDUE_D20: 'Vencido 20 dias (D+20)',
  PAYMENT_CONFIRMED: 'Pagamento confirmado',
  NFSE_ISSUED: 'NFS-e emitida',
  RECEIPT_AVAILABLE: 'Recibo disponível',
  TITLE_UPDATED: 'Boleto atualizado',
  GROUPED: 'Agrupado (múltiplos boletos)',
};

const AVAILABLE_VARS = [
  'responsavel_nome', 'aluno_nome', 'descricao', 'competencia', 'vencimento',
  'valor', 'dias_vencimento', 'dias_atraso', 'status', 'quantidade_titulos', 'valor_total',
];

export default function DnaPayNotificationsAdmin() {
  const [tab, setTab] = useState('regua');
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState({ status: '', type: '', title_id: '', guardian_id: '' });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, m] = await Promise.all([
        api.get('/dnapay/notifications-admin/settings'),
        api.get('/dnapay/notifications-admin/templates'),
        api.get('/dnapay/notifications-admin/metrics'),
      ]);
      setSettings(s.data);
      setTemplates(t.data.templates || []);
      setMetrics(m.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao carregar configurações');
    } finally { setLoading(false); }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v));
      const { data } = await api.get('/dnapay/notifications-admin/jobs', { params: { ...params, limit: 200 } });
      setJobs(data.items || []);
      setJobsTotal(data.total || 0);
    } catch (e) {
      toast.error('Falha ao carregar jobs');
    }
  }, [filter]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (tab === 'jobs') loadJobs(); }, [tab, loadJobs]);

  // ─────────── handlers régua ───────────
  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const payload = {
        enabled: settings.enabled,
        days_before: settings.days_before,
        days_after: settings.days_after,
        send_window_start: settings.send_window_start,
        send_window_end: settings.send_window_end,
        allowed_weekdays: settings.allowed_weekdays,
        max_per_title_per_day: settings.max_per_title_per_day,
        min_hours_between_same_type: settings.min_hours_between_same_type,
        min_amount_cents: settings.min_amount_cents,
        send_payment_confirmed: settings.send_payment_confirmed,
        send_nfse: settings.send_nfse,
        send_receipt: settings.send_receipt,
        group_multiple_titles: settings.group_multiple_titles,
        categories_included: settings.categories_included || [],
        rule_version: settings.rule_version,
      };
      const { data } = await api.put('/dnapay/notifications-admin/settings', payload);
      setSettings(data);
      toast.success('Régua salva com sucesso.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao salvar');
    } finally { setSaving(false); }
  };

  const toggleDay = (d) => {
    const cur = new Set(settings.allowed_weekdays || []);
    if (cur.has(d)) cur.delete(d); else cur.add(d);
    setSettings({ ...settings, allowed_weekdays: Array.from(cur).sort() });
  };

  const toggleDayValue = (arr, d, key) => {
    const cur = new Set(settings[key] || []);
    if (cur.has(d)) cur.delete(d); else cur.add(d);
    setSettings({ ...settings, [key]: Array.from(cur).sort((a, b) => a - b) });
  };

  const runRegua = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/dnapay/notifications-admin/regua/run-now');
      toast.success(`Régua executada: ${data.created} novos jobs criados (${data.seen} títulos avaliados)`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao rodar régua');
    } finally { setSaving(false); }
  };

  const dispatchNow = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/dnapay/notifications-admin/dispatch-now');
      if (data.skipped) toast.warning(`Dispatch pulado: ${data.reason || ''}`);
      else toast.success(`Dispatch: ${data.sent} enviados · ${data.failed} falhas · ${data.skipped} pulados`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha no dispatch');
    } finally { setSaving(false); }
  };

  // ─────────── templates ───────────
  const saveTemplate = async (t) => {
    try {
      await api.put(`/dnapay/notifications-admin/templates/${t.event}`, { title: t.title, body: t.body });
      toast.success(`Template "${EVENT_LABEL[t.event] || t.event}" salvo`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao salvar template');
    }
  };

  const cancelJob = async (id) => {
    try {
      await api.post(`/dnapay/notifications-admin/jobs/${id}/cancel`);
      toast.success('Job cancelado');
      loadJobs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha'); }
  };

  const resendJob = async (id) => {
    try {
      await api.post(`/dnapay/notifications-admin/jobs/${id}/resend`);
      toast.success('Job reenfileirado');
      loadJobs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao reenviar'); }
  };

  if (loading || !settings) {
    return <div className="p-6 max-w-6xl mx-auto text-slate-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Carregando configurações...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="notifications-admin">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><Bell className="w-5 h-5" /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Notificações Financeiras</h1>
          <p className="text-sm text-slate-500">Configure a régua, templates e envie manualmente para os responsáveis via DNA Connect App.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${settings.enabled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
            {settings.enabled ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
            {settings.enabled ? 'Régua ativa' : 'Régua desativada'}
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${metrics?.fcm_enabled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
            <Sparkles className="w-3 h-3" />
            FCM {metrics?.fcm_enabled ? 'ATIVO' : 'stub (validação)'}
          </span>
        </div>
      </div>

      {/* Métricas rápidas */}
      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-6">
          {[
            { k: 'PENDING', label: 'Agendadas', color: 'bg-slate-50 text-slate-700 border-slate-200' },
            { k: 'AWAITING_TOKEN', label: 'Aguard. token', color: 'bg-amber-50 text-amber-700 border-amber-200' },
            { k: 'SENT', label: 'Enviadas', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
            { k: 'OPENED', label: 'Abertas', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
            { k: 'FAILED', label: 'Falhas', color: 'bg-red-50 text-red-700 border-red-200' },
            { k: 'SKIPPED_PAID', label: 'Canceladas (pago)', color: 'bg-slate-50 text-slate-500 border-slate-200' },
          ].map(m => (
            <div key={m.k} className={`rounded-xl border p-3 ${m.color}`} data-testid={`metric-${m.k}`}>
              <div className="text-[11px] uppercase tracking-wide opacity-80">{m.label}</div>
              <div className="text-2xl font-bold">{metrics.counts_by_status?.[m.k] || 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* V4.43 · Diagnóstico do último run */}
      {metrics?.last_regua_diagnostics && (
        <div className="bg-slate-900 text-white rounded-2xl p-4 mb-6 text-xs" data-testid="regua-diagnostics">
          <div className="font-semibold mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-300" />
            Diagnóstico da última execução da régua
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>Títulos avaliados: <b>{metrics.last_regua_diagnostics.titles_evaluated}</b></div>
            <div>Elegíveis: <b className="text-emerald-300">{metrics.last_regua_diagnostics.eligible}</b></div>
            <div>Jobs criados: <b className="text-emerald-300">{metrics.last_regua_diagnostics.jobs_created}</b></div>
          </div>
          {metrics.last_regua_diagnostics.skipped_reasons && (
            <div className="mt-3">
              <div className="text-slate-300 mb-1">Descartes por motivo:</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1">
                {Object.entries(metrics.last_regua_diagnostics.skipped_reasons)
                  .filter(([_, v]) => v > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span className="text-slate-400">{k}</span><span>{v}</span></div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-1 mb-6">
        {[
          { k: 'regua', label: 'Régua', icon: Calendar },
          { k: 'templates', label: 'Templates', icon: Sparkles },
          { k: 'jobs', label: 'Envios', icon: Send },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition ${tab === t.k ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t.k}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'regua' && (
        <div className="space-y-5" data-testid="tab-content-regua">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900">Ativação</h3>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!settings.enabled} onChange={e => setSettings({ ...settings, enabled: e.target.checked })} data-testid="toggle-enabled" />
                <span className="text-sm">{settings.enabled ? 'Régua ativa' : 'Régua desativada'}</span>
              </label>
            </div>
            <p className="text-xs text-slate-500">Se desativada, nenhum job é criado ou enviado — nem por eventos, nem pela régua diária.</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-base font-semibold text-slate-900 mb-4">Dias antes do vencimento (lembretes)</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {(settings.days_before || []).sort((a,b)=>b-a).map(d => (
                <span key={d} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border bg-indigo-600 text-white border-indigo-600" data-testid={`day-before-${d}`}>
                  {d === 0 ? 'D0 (hoje)' : `D-${d}`}
                  <button type="button" onClick={() => setSettings({ ...settings, days_before: settings.days_before.filter(x => x !== d) })} className="ml-1 hover:text-red-200" aria-label="remover">×</button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input type="number" min="0" max="365" placeholder="Ex: 15" id="dc-days-before" className="w-24 border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid="input-day-before" />
              <button type="button" onClick={() => {
                const el = document.getElementById('dc-days-before');
                const v = parseInt(el.value || '0', 10);
                if (isNaN(v) || v < 0 || v > 365) { toast.error('Informe um número entre 0 e 365.'); return; }
                if ((settings.days_before || []).includes(v)) { toast.info('Dia já configurado.'); return; }
                setSettings({ ...settings, days_before: [...(settings.days_before || []), v].sort((a,b)=>a-b) });
                el.value = '';
              }} className="px-3 py-1.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 text-sm font-medium hover:bg-indigo-100" data-testid="add-day-before">+ Adicionar dia</button>
              <span className="text-xs text-slate-500">Ex: para lembrar 15 dias antes, digite <b>15</b></span>
            </div>
            <h3 className="text-base font-semibold text-slate-900 mt-6 mb-4">Dias após o vencimento (cobrança)</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {(settings.days_after || []).sort((a,b)=>a-b).map(d => (
                <span key={d} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border bg-red-600 text-white border-red-600" data-testid={`day-after-${d}`}>
                  D+{d}
                  <button type="button" onClick={() => setSettings({ ...settings, days_after: settings.days_after.filter(x => x !== d) })} className="ml-1 hover:text-red-200" aria-label="remover">×</button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input type="number" min="1" max="365" placeholder="Ex: 45" id="dc-days-after" className="w-24 border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid="input-day-after" />
              <button type="button" onClick={() => {
                const el = document.getElementById('dc-days-after');
                const v = parseInt(el.value || '0', 10);
                if (isNaN(v) || v < 1 || v > 365) { toast.error('Informe um número entre 1 e 365.'); return; }
                if ((settings.days_after || []).includes(v)) { toast.info('Dia já configurado.'); return; }
                setSettings({ ...settings, days_after: [...(settings.days_after || []), v].sort((a,b)=>a-b) });
                el.value = '';
              }} className="px-3 py-1.5 rounded bg-red-50 text-red-700 border border-red-200 text-sm font-medium hover:bg-red-100" data-testid="add-day-after">+ Adicionar dia</button>
              <span className="text-xs text-slate-500">Templates específicos para 7/3/1/0 e 1/5/10/20; outros dias usam texto padrão.</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-base font-semibold text-slate-900 mb-4"><Clock className="w-4 h-4 inline mr-1" /> Janela de envio (America/Sao_Paulo)</h3>
            <div className="flex items-center gap-4">
              <label className="text-sm">
                <span className="block text-xs text-slate-500 mb-1">Início</span>
                <input type="time" value={settings.send_window_start} onChange={e => setSettings({ ...settings, send_window_start: e.target.value })} className="border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="window-start" />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-slate-500 mb-1">Fim</span>
                <input type="time" value={settings.send_window_end} onChange={e => setSettings({ ...settings, send_window_end: e.target.value })} className="border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="window-end" />
              </label>
            </div>
            <h4 className="text-sm font-medium text-slate-900 mt-5 mb-2">Dias permitidos</h4>
            <div className="flex flex-wrap gap-2">
              {WEEKDAY_LABELS.map((lbl, i) => {
                const active = (settings.allowed_weekdays || []).includes(i);
                return (
                  <button key={i} type="button" onClick={() => toggleDay(i)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${active ? 'bg-indigo-100 text-indigo-800 border-indigo-300' : 'bg-slate-50 text-slate-500 border-slate-200'}`}
                    data-testid={`weekday-${i}`}>
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs text-slate-500">Máx. por título por dia</span>
              <input type="number" value={settings.max_per_title_per_day} onChange={e => setSettings({ ...settings, max_per_title_per_day: parseInt(e.target.value || '0', 10) })} className="mt-1 w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="max-per-title-per-day" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Intervalo mínimo (horas)</span>
              <input type="number" value={settings.min_hours_between_same_type} onChange={e => setSettings({ ...settings, min_hours_between_same_type: parseInt(e.target.value || '0', 10) })} className="mt-1 w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="min-hours" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Valor mínimo (centavos)</span>
              <input type="number" value={settings.min_amount_cents} onChange={e => setSettings({ ...settings, min_amount_cents: parseInt(e.target.value || '0', 10) })} className="mt-1 w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="min-amount-cents" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Versão da régua (bumpa idempotência)</span>
              <input type="number" value={settings.rule_version} onChange={e => setSettings({ ...settings, rule_version: parseInt(e.target.value || '1', 10) })} className="mt-1 w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="rule-version" />
            </label>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-base font-semibold text-slate-900 mb-3">Eventos habilitados</h3>
            {[
              { k: 'send_payment_confirmed', label: 'Pagamento confirmado' },
              { k: 'send_nfse', label: 'NFS-e emitida' },
              { k: 'send_receipt', label: 'Recibo disponível' },
              { k: 'group_multiple_titles', label: 'Agrupar múltiplos boletos por família' },
            ].map(o => (
              <label key={o.k} className="flex items-center gap-2 py-1.5 cursor-pointer text-sm">
                <input type="checkbox" checked={!!settings[o.k]} onChange={e => setSettings({ ...settings, [o.k]: e.target.checked })} data-testid={`toggle-${o.k}`} />
                {o.label}
              </label>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 items-center bg-white rounded-2xl border border-slate-200 p-4">
            <button type="button" onClick={saveSettings} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="save-settings">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar régua
            </button>
            <button type="button" onClick={runRegua} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50" data-testid="run-regua-now">
              <PlayCircle className="w-4 h-4" /> Rodar régua agora
            </button>
            <button type="button" onClick={dispatchNow} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-900 disabled:opacity-50" data-testid="dispatch-now">
              <Send className="w-4 h-4" /> Disparar envios pendentes
            </button>
            <div className="text-xs text-slate-500 ml-auto flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Janela horária respeitada no dispatch.</div>
          </div>
        </div>
      )}

      {tab === 'templates' && (
        <div className="space-y-3" data-testid="tab-content-templates">
          <div className="text-xs text-slate-500 mb-2 bg-indigo-50 border border-indigo-200 rounded p-3">
            Variáveis disponíveis: {AVAILABLE_VARS.map(v => <code key={v} className="mx-1 px-1 bg-white rounded border border-slate-200">{'{' + v + '}'}</code>)}
          </div>
          {templates.map((t, idx) => (
            <TemplateCard key={t.event} t={t} onChange={(nv) => {
              const nl = templates.slice();
              nl[idx] = { ...t, ...nv };
              setTemplates(nl);
            }} onSave={() => saveTemplate(templates[idx])} />
          ))}
        </div>
      )}

      {tab === 'jobs' && (
        <div className="space-y-3" data-testid="tab-content-jobs">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 grid sm:grid-cols-5 gap-2">
            <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} className="border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid="filter-status">
              <option value="">Todos os status</option>
              {['PENDING', 'SENT', 'OPENED', 'FAILED', 'CANCELLED', 'SKIPPED_PAID', 'SKIPPED_PREFERENCE', 'SKIPPED_NO_DEVICE'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filter.type} onChange={e => setFilter({ ...filter, type: e.target.value })} className="border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid="filter-type">
              <option value="">Todos os tipos</option>
              {Object.keys(EVENT_LABEL).map(k => <option key={k} value={k}>{EVENT_LABEL[k]}</option>)}
            </select>
            <input placeholder="title_id" value={filter.title_id} onChange={e => setFilter({ ...filter, title_id: e.target.value })} className="border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid="filter-title" />
            <input placeholder="guardian_id" value={filter.guardian_id} onChange={e => setFilter({ ...filter, guardian_id: e.target.value })} className="border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid="filter-guardian" />
            <button type="button" onClick={loadJobs} className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700" data-testid="filter-apply"><Search className="w-4 h-4" /> Filtrar</button>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Criado em</th>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-left px-3 py-2">Título/Body</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Tentativas</th>
                  <th className="text-right px-3 py-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Nenhum job com os filtros aplicados.</td></tr>
                )}
                {jobs.map(j => (
                  <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`job-row-${j.id}`}>
                    <td className="px-3 py-2 text-xs text-slate-500">{(j.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-3 py-2 text-xs"><span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">{EVENT_LABEL[j.type] || j.type}</span></td>
                    <td className="px-3 py-2 max-w-md truncate">
                      <div className="font-medium text-slate-900 truncate">{j.title}</div>
                      <div className="text-xs text-slate-500 truncate">{j.body}</div>
                    </td>
                    <td className="px-3 py-2"><StatusBadge status={j.status} /></td>
                    <td className="px-3 py-2 text-center text-xs">{j.attempts || 0}</td>
                    <td className="px-3 py-2 text-right space-x-1">
                      {(j.status === 'PENDING' || j.status === 'FAILED') && <button type="button" onClick={() => cancelJob(j.id)} className="text-xs text-red-700 hover:underline" data-testid={`cancel-${j.id}`}>Cancelar</button>}
                      {(j.status === 'FAILED') && <button type="button" onClick={() => resendJob(j.id)} className="text-xs text-indigo-700 hover:underline" data-testid={`resend-${j.id}`}><Undo2 className="w-3 h-3 inline" /> Reenviar</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-slate-500">Total: {jobsTotal}</div>
        </div>
      )}
    </div>
  );
}

function TemplateCard({ t, onChange, onSave }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`template-${t.event}`}>
      <div className="text-sm font-semibold text-slate-900 mb-1">{EVENT_LABEL[t.event] || t.event}</div>
      <div className="text-[10px] font-mono text-slate-400 mb-3">event: {t.event}</div>
      <label className="block mb-2">
        <span className="text-xs text-slate-500">Título</span>
        <input value={t.title} onChange={e => onChange({ title: e.target.value })} className="mt-1 w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid={`tpl-title-${t.event}`} />
      </label>
      <label className="block mb-3">
        <span className="text-xs text-slate-500">Corpo</span>
        <textarea rows={2} value={t.body} onChange={e => onChange({ body: e.target.value })} className="mt-1 w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid={`tpl-body-${t.event}`} />
      </label>
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-slate-400">Padrão: <span className="italic">{t.default_body}</span></div>
        <button type="button" onClick={onSave} className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1" data-testid={`tpl-save-${t.event}`}>
          <Save className="w-3 h-3" /> Salvar
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    PENDING: 'dna-tone-neutral',
    SENT: 'dna-tone-success',
    OPENED: 'dna-tone-info',
    FAILED: 'dna-tone-danger',
    CANCELLED: 'dna-tone-neutral',
    SKIPPED_PAID: 'dna-tone-warning',
    SKIPPED_PREFERENCE: 'dna-tone-warning',
    SKIPPED_NO_DEVICE: 'dna-tone-warning',
  };
  return <span className={`text-[11px] px-2 py-0.5 rounded border ${map[status] || 'dna-tone-neutral'}`}>{status}</span>;
}
